import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Alert from '@mui/material/Alert'

import { useAppSelector } from '../hooks'
import { useStatusBannerHost } from '../hooks/useStatusBannerHost'
import { useZoomIndicatorState } from '../hooks/useZoomIndicatorState'
import { bannerFlexOrder } from '../utils/statusBannerStack'
import { zoomIndicatorMessage } from '../utils/zoomResetHint'
import { statusBannerAlertSx } from './styles/statusBanner.styles'

/** Safe on a server render, where the component would never reach the message anyway. */
function readNavigator(): { userAgent: string; maxTouchPoints: number } {
  if (typeof navigator === 'undefined') return { userAgent: '', maxTouchPoints: 0 }
  return { userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints ?? 0 }
}

/**
 * SC-255 — tells the user their controls went off-screen because the page is zoomed.
 *
 * Self-gates on `roomJoined`: this is in-game chrome, and the main menu has no bottom lane to
 * lose. Gating here rather than at the mount site keeps the rule in one testable place.
 *
 * Renders through a portal into `StatusBannerViewport`, which is pinned to the visible viewport
 * and counter-scaled. That matters more here than for the other banners: a `position: fixed`
 * element stays anchored to the *layout* viewport under WebKit magnification and slides
 * off-screen along with the very controls it is reporting on — invisible at exactly the moment it
 * is needed. Sharing the column also means it cannot overlap the banners above it, and matches
 * their size, without any offset arithmetic.
 *
 * Takes the bottom slot of the column: blocked audio and a dropped connection both cost the user
 * more to miss than being zoomed in.
 */
const ZoomedControlsIndicator: React.FC = () => {
  const roomJoined = useAppSelector((state) => state.room.roomJoined)
  const reason = useZoomIndicatorState(roomJoined)
  const [dismissed, setDismissed] = useState(false)
  const host = useStatusBannerHost()

  // Re-arm once the condition clears, so dismissing now does not suppress the next occurrence.
  useEffect(() => {
    if (reason === null) setDismissed(false)
  }, [reason])

  if (!roomJoined || reason === null || dismissed || !host) return null

  return createPortal(
    <Alert
      severity="warning"
      variant="filled"
      // MUI defaults an Alert to `role="alert"`, which implies `aria-live="assertive"` — an
      // interrupt. Pairing that with `aria-live="polite"` asks screen readers to resolve a
      // contradiction, and they do not agree on how. `role="status"` implies polite natively, and
      // is what the other two banners in the column already use.
      role="status"
      aria-live="polite"
      onClose={() => setDismissed(true)}
      sx={{
        ...statusBannerAlertSx,
        order: bannerFlexOrder('zoom'),
        maxWidth: 'min(560px, calc(100% - 32px))',
      }}
    >
      {/*
        Plain prose, not a "Reset zoom" button and not a styled keycap. No browser lets a page
        change its own zoom, and a synthesised Cmd+0 is an untrusted event that browsers
        deliberately refuse to act on — so a control here could not honour its label. Telling the
        user what to do, in the same voice as the rest of the sentence, is the strongest honest
        affordance. One sentence for everyone: the only thing that varies is the shortcut name,
        and touch-only devices are told to pinch because they have no key to press. It does not
        vary by which detection arm fired. See `utils/zoomResetHint`.
      */}
      {zoomIndicatorMessage(readNavigator())}
    </Alert>,
    host,
  )
}

export default ZoomedControlsIndicator
